import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import { useTheme } from "../DataContext";
import { DEMO_RISKS } from "../demoData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, PieChart, Pie, Cell,
} from "recharts";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

export default function RiskIntelligence() {
  const { theme } = useTheme();
  const [selectedRisk, setSelectedRisk] = useState(null);

  const criticalRisks = DEMO_RISKS.filter(r => r.score >= 50);
  const totalExposure = DEMO_RISKS.reduce((s, r) => s + r.budget_exposure, 0);
  const avgScore      = Math.round(DEMO_RISKS.reduce((s, r) => s + r.score, 0) / DEMO_RISKS.length);

  const catData = Object.entries(
    DEMO_RISKS.reduce((acc, r) => {
      acc[r.category] = (acc[r.category] || 0) + 1;
      return acc;
    }, {})
  ).map(([cat, count]) => ({ cat, count }));

  const VELOCITY_COLOR = { rising: theme.danger, stable: theme.warning, falling: theme.success };

  const scatterData = DEMO_RISKS.map(r => ({
    x: r.prob * 100,
    y: r.impact * 100,
    z: r.budget_exposure / 10000,
    name: r.title,
    score: r.score,
    id: r.id,
  }));

  const pieColors = [theme.accent, theme.danger, theme.warning, theme.success, theme.cyan, "#A78BFA"];

  return (
    <PageLayout
      title="Risk Intelligence"
      subtitle="Quantified risk scores, velocity tracking, and automated mitigation recommendations — P-04"
    >
      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Total Risks"       value={DEMO_RISKS.length} icon="⚠" color={theme.accent}  sub="Across portfolio" />
        <KPICard label="Critical Risks"    value={criticalRisks.length} icon="✗" color={theme.danger}  sub="Score ≥ 50" />
        <KPICard label="Rising Velocity"   value={DEMO_RISKS.filter(r => r.velocity === "rising").length} icon="▲" color={theme.warning} sub="Escalating risk" />
        <KPICard label="Budget Exposure"   value={fmt(totalExposure)} icon="▦" color={theme.danger}  sub="Total financial risk" />
        <KPICard label="Avg Risk Score"    value={avgScore} icon="◎" color={avgScore >= 40 ? theme.danger : theme.warning} sub="Probability × Impact" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* Risk Matrix Scatter */}
        <div className="pmo-card">
          <div className="section-title">Risk Matrix — Probability vs Impact</div>
          <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "8px" }}>Bubble size = budget exposure · Hover for details</div>
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              {/* Risk zones */}
              <XAxis type="number" dataKey="x" name="Probability %" domain={[0, 100]}
                tick={{ fill: theme.textMuted, fontSize: 10 }}
                label={{ value: "Probability %", fill: theme.textMuted, fontSize: 11, position: "bottom", offset: -5 }} />
              <YAxis type="number" dataKey="y" name="Impact %" domain={[0, 100]}
                tick={{ fill: theme.textMuted, fontSize: 10 }}
                label={{ value: "Impact %", angle: -90, fill: theme.textMuted, fontSize: 11 }} />
              <ZAxis type="number" dataKey="z" range={[30, 200]} />
              <Tooltip content={({ payload }) => {
                if (!payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, padding: "10px 14px", borderRadius: "8px", fontSize: "11px", maxWidth: "220px" }}>
                    <div style={{ color: theme.accent, fontWeight: 600, marginBottom: "4px" }}>{d.id}</div>
                    <div style={{ color: theme.textPrimary, marginBottom: "4px" }}>{d.name}</div>
                    <div>Score: <span style={{ color: d.score >= 50 ? theme.danger : d.score >= 30 ? theme.warning : theme.success }}>{d.score.toFixed(1)}</span></div>
                  </div>
                );
              }} />
              <Scatter
                data={scatterData}
                shape={(props) => {
                  const { cx, cy, r, payload } = props;
                  const color = payload.score >= 50 ? theme.danger : payload.score >= 30 ? theme.warning : theme.success;
                  return (
                    <g onClick={() => setSelectedRisk(DEMO_RISKS.find(r => r.id === payload.id))} style={{ cursor: "pointer" }}>
                      <circle cx={cx} cy={cy} r={Math.max(r / 2, 6)} fill={`${color}55`} stroke={color} strokeWidth={1.5} />
                      <text x={cx} y={cy - r / 2 - 4} textAnchor="middle" fill={color} fontSize="9" fontWeight="600">{payload.id}</text>
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Category Pie + Exposure Bar */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="pmo-card">
            <div className="section-title">Risk by Category</div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <ResponsiveContainer width={120} height={120}>
                <PieChart>
                  <Pie data={catData} cx="50%" cy="50%" outerRadius={50} dataKey="count" paddingAngle={2}>
                    {catData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {catData.map((c, i) => (
                  <div key={c.cat} style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                    <span style={{ fontSize: "11px", color: theme.textSecondary, display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "6px", height: "6px", borderRadius: "1px", background: pieColors[i % pieColors.length] }} />
                      {c.cat}
                    </span>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: pieColors[i % pieColors.length] }}>{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pmo-card" style={{ flex: 1 }}>
            <div className="section-title">Budget Exposure by Risk</div>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={DEMO_RISKS.slice(0, 5)} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 10 }}>
                <XAxis type="number" tickFormatter={v => fmt(v)} tick={{ fill: theme.textMuted, fontSize: 9 }} />
                <YAxis type="category" dataKey="id" tick={{ fill: theme.textMuted, fontSize: 10 }} width={35} />
                <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
                <Bar dataKey="budget_exposure" name="Exposure" radius={[0,4,4,0]}>
                  {DEMO_RISKS.slice(0, 5).map((r, i) => (
                    <Cell key={i} fill={r.score >= 50 ? theme.danger : r.score >= 30 ? theme.warning : theme.success} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Selected Risk Detail */}
      {selectedRisk && (
        <div className="pmo-card" style={{ marginBottom: "20px", borderColor: theme.accent }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <div className="section-title" style={{ marginBottom: 0 }}>Risk Detail — {selectedRisk.id}</div>
            <button onClick={() => setSelectedRisk(null)} style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Title</div>
              <div style={{ color: theme.textPrimary, fontWeight: 600 }}>{selectedRisk.title}</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Risk Score</div>
              <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "20px", fontWeight: 700, color: selectedRisk.score >= 50 ? theme.danger : theme.warning }}>{selectedRisk.score.toFixed(1)}</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Velocity</div>
              <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600, background: `${VELOCITY_COLOR[selectedRisk.velocity]}22`, color: VELOCITY_COLOR[selectedRisk.velocity] }}>{selectedRisk.velocity.toUpperCase()}</span>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Budget Exposure</div>
              <div style={{ color: theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{fmt(selectedRisk.budget_exposure)}</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Probability × Impact</div>
              <div style={{ color: theme.textSecondary }}>{(selectedRisk.prob * 100).toFixed(0)}% × {(selectedRisk.impact * 100).toFixed(0)}%</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Category</div>
              <div style={{ color: theme.accent }}>{selectedRisk.category}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>AI-Recommended Mitigation</div>
              <div style={{ padding: "10px 14px", background: `${theme.accent}10`, border: `1px solid ${theme.accent}33`, borderRadius: "8px", fontSize: "12px", color: theme.textSecondary }}>
                {selectedRisk.mitigation}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Register Table */}
      <div className="pmo-card">
        <div className="section-title">Risk Register — Full Portfolio</div>
        <table>
          <thead>
            <tr>{["ID","Project","Risk Title","Category","Prob","Impact","Score","Budget Exposure","Velocity","Mitigation","Action"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEMO_RISKS.sort((a, b) => b.score - a.score).map(r => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => setSelectedRisk(r)}>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{r.id}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{r.project}</td>
                <td style={{ color: theme.textPrimary, fontWeight: 500, maxWidth: "200px" }}>{r.title}</td>
                <td><span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "4px", background: `${theme.accent}15`, color: theme.accent }}>{r.category}</span></td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{(r.prob * 100).toFixed(0)}%</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{(r.impact * 100).toFixed(0)}%</td>
                <td style={{ fontFamily: "'Orbitron',monospace", color: r.score >= 50 ? theme.danger : r.score >= 30 ? theme.warning : theme.success, fontWeight: 700 }}>{r.score.toFixed(1)}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.danger }}>{fmt(r.budget_exposure)}</td>
                <td>
                  <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 700, background: `${VELOCITY_COLOR[r.velocity]}18`, color: VELOCITY_COLOR[r.velocity] }}>
                    {r.velocity.toUpperCase()}
                  </span>
                </td>
                <td style={{ fontSize: "11px", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.mitigation}</td>
                <td>
                  <button onClick={e => { e.stopPropagation(); setSelectedRisk(r); }} style={{ padding: "3px 8px", borderRadius: "4px", border: `1px solid ${theme.accent}44`, background: "transparent", color: theme.accent, fontSize: "10px", cursor: "pointer" }}>
                    DETAIL
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
